<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestFieldGrid from '@/components/finest-field-grid/FinestFieldGrid.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableValue,
} from '@/components/finest-field-grid/types'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { usePersonUpdate } from '@/pages/investors/profile/usePersonUpdate'
import type { ExtendedEnum } from '@/types'
import type { FieldStateMap } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import NaturalPersonData = App.Data.User.NaturalPersonData

const props = defineProps<{
  fieldStates: FieldStateMap
  person: NaturalPersonData
  employmentOptions: ExtendedEnum[]
}>()

const { t, te } = useI18n()

const EMPLOYEE = 'employee'
const PENSIONER = 'pensioner'

const editableKeys = ['profession', 'occupation', 'employer', 'planned_retirement_date']

const seedModel = (): InlineEditableModel => {
  const source = props.person as Record<string, InlineEditableValue>
  return Object.fromEntries(editableKeys.map((key) => [key, source[key] ?? null]))
}

const { model: employmentModel, confirm } = useSyncedModel(seedModel)

const update = usePersonUpdate(props.person.id ?? '', confirm)

const isPensioner = computed(() => employmentModel.value.profession === PENSIONER)
const isEmployee = computed(() => employmentModel.value.profession === EMPLOYEE)

const professionOptions = computed(() => {
  const options = props.employmentOptions.map((option) => ({
    value: option.id,
    label: t(`enums.professionType.${option.id}`),
  }))

  const stored = props.person.profession

  if (stored && !options.some((option) => option.value === stored) && te(`enums.professionType.${stored}`)) {
    options.push({ value: stored, label: t(`enums.professionType.${stored}`) })
  }

  return options
})

const employmentFields = computed<InlineEditableField[]>(() => [
  {
    key: 'profession',
    label: t('fields.profession'),
    type: 'combobox',
    fullWidth: true,
    options: professionOptions.value,
  },
  ...(isPensioner.value
    ? []
    : [{ key: 'occupation', label: t('fields.occupation'), type: 'text' as const, fullWidth: true }]),
  ...(isEmployee.value
    ? [{ key: 'employer', label: t('fields.employer'), type: 'text' as const, fullWidth: true }]
    : []),
  ...(isPensioner.value
    ? []
    : [
        {
          key: 'planned_retirement_date',
          label: t('fields.planned_retirement_date'),
          type: 'date' as const,
        },
      ]),
])

const save = ({ key, value }: InlineEditableCommitPayload) => update({ [key]: value })
</script>

<template>
  <div class="grid items-start gap-4 lg:grid-cols-2">
    <FinestCard :title="t('cards.employment')" icon="pi pi-briefcase">
      <FinestFieldGrid
        v-model="employmentModel"
        :fields="employmentFields"
        :field-states="fieldStates"
        @commit="save" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "cards": {
      "employment": "Arbeitsverhältnis"
    },
    "fields": {
      "profession": "Arbeitsverhältnis",
      "occupation": "Beruf, Branche",
      "employer": "Beschäftigt bei",
      "planned_retirement_date": "Geplanter Renteneintritt"
    }
  },
  "en": {
    "cards": {
      "employment": "Employment"
    },
    "fields": {
      "profession": "Employment",
      "occupation": "Profession, industry",
      "employer": "Employed at",
      "planned_retirement_date": "Planned retirement"
    }
  }
}
</i18n>
